import { margin, size, yAxisRange } from './types.js';
import { getRenderGroupsMinMaxValue } from './render-groups/utils.js';
import { RenderGroup } from './render-groups/types.js';
import { ValueFormatter } from '../common/format.js';
import { getValueTicks } from './axis/value/getValueTicks.js';
import { getRotatedBoxHeight } from '../common/getRotatedBoxSize.js';
import { calculateLegendBounds } from './legend/legendSizes.js';
import { legendsConfiguration } from './legend/types.js';
import { KeyValueSet } from './axis/key/KeyValueSet.js';
import { getTicksMaxWidth } from './axis/value/utils.js';
export class MarginCalculator {
  #topMargin = 0;
  #rightMargin = 0;
  #bottomMargin = 0;
  #leftMargin = 0;
  constructor(private svgSize: size, private valueFormatter: ValueFormatter, private leftRenderGroups: RenderGroup[], private rightRenderGroups: RenderGroup[], private legendsConfiguration: legendsConfiguration, private legendNames: string[], private hideLegends: boolean, private hideAxisLeft: boolean, private hideAxisRight: boolean, private keyValueSet: KeyValueSet, private axisLeftRange?: yAxisRange, private axisRightRange?: yAxisRange) {}
  public calculate(): margin {
    this.#updateLeftMargin(false);
    this.#updateRightMargin();
    this.#updateTopMargin();
    const keySize = this.#availableWidth / this.keyValueSet.length;
    this.keyValueSet.updateKeyRotation(keySize);
    this.#updateBottomMargin();
    this.#updateLeftMargin(true);
    this.#updateRightMargin();
    this.#updateTopMargin();
    this.#updateBottomMargin();
    return {
      top: this.#topMargin,
      left: this.#leftMargin,
      right: this.#rightMargin,
      bottom: this.#bottomMargin
    };
  }
  #updateTopMargin() {
    this.#topMargin = calculateTopMargin(this.legendsConfiguration, this.legendNames, this.#availableWidth + this.#rightMargin, this.svgSize.height * 0.4, this.hideLegends);
  }
  #updateBottomMargin() {
    this.#bottomMargin = calculateMarginBottom(this.keyValueSet);
  }
  #updateLeftMargin(considerFirstKey: boolean) {
    if (considerFirstKey) {
      const newValue = calculateLeftMarginBasedOnFirstKey(this.#availableWidth, this.#leftMargin, this.keyValueSet);
      if (newValue > this.#leftMargin) {
        this.#leftMargin = newValue;
        return;
      }
    }
    this.#leftMargin = calculateLeftMargin(this.#availableHeight, this.valueFormatter, this.leftRenderGroups, this.hideAxisLeft, this.axisLeftRange);
  }
  #updateRightMargin() {
    this.#rightMargin = calculateRightMargin(this.#availableHeight, this.valueFormatter, this.rightRenderGroups, this.hideAxisRight, this.axisRightRange);
  }
  get #availableWidth() {
    return this.svgSize.width - (this.#leftMargin + this.#rightMargin);
  }
  get #availableHeight() {
    return this.svgSize.height - (this.#bottomMargin + this.#topMargin);
  }
}
const exteriorTopMargin = 16;
const exteriorRightMargin = 4;
const exteriorBottomMargin = 4;
const exteriorLeftMargin = 4;
const calculateLeftMargin = (availableHeight: number, valueFormatter: ValueFormatter, leftRenderGroups: RenderGroup[], hideAxisLeft: boolean, axisLeftRange?: yAxisRange) => {
  if (!leftRenderGroups.length || hideAxisLeft) {
    return 0;
  }
  const {
    minValue: leftMinValue,
    maxValue: leftMaxValue
  } = getRenderGroupsMinMaxValue(leftRenderGroups);
  const leftValueTicks = getValueTicks(availableHeight, leftMinValue, leftMaxValue, axisLeftRange?.min, axisLeftRange?.max);
  const marginLeft = getTicksMaxWidth(leftValueTicks, valueFormatter);
  return marginLeft > 0 ? marginLeft + exteriorLeftMargin : 0;
};
const calculateLeftMarginBasedOnFirstKey = (availableWidth: number, currentMarginLeft: number, keyValueSet: KeyValueSet) => {
  const keySize = availableWidth / keyValueSet.length;
  if (keyValueSet.keyRotation === 45) {
    const {
      boxWidth: boxWidthOfFirstKey
    } = keyValueSet.getRotatedSize(keyValueSet.keys[0], keyValueSet.keyRotation);
    const innerKeyPosition = keyValueSet.isWrapped ? keySize : keySize / 2;
    const availableSpaceToLeft = currentMarginLeft + innerKeyPosition;
    if (availableSpaceToLeft < boxWidthOfFirstKey) {
      return currentMarginLeft + Math.abs(boxWidthOfFirstKey - availableSpaceToLeft);
    }
  }
  return currentMarginLeft;
};
const calculateRightMargin = (availableHeight: number, valueFormatter: ValueFormatter, rightRenderGroups: RenderGroup[], hideAxisRight: boolean, axisRightRange?: yAxisRange) => {
  if (!rightRenderGroups.length || hideAxisRight) {
    return 0;
  }
  const {
    minValue: rightMinValue,
    maxValue: rightMaxValue
  } = getRenderGroupsMinMaxValue(rightRenderGroups);
  const rightValueTicks = getValueTicks(availableHeight, rightMinValue, rightMaxValue, axisRightRange?.min, axisRightRange?.max);
  const marginRight = getTicksMaxWidth(rightValueTicks, valueFormatter);
  return marginRight > 0 ? marginRight + exteriorRightMargin : 0;
};
const calculateTopMargin = (legendsConfiguration: legendsConfiguration, legendNames: string[], availableWidth: number, availableHeight: number, hideLegends: boolean) => {
  /* reset column count since we assume inline flow layout for legends */
  legendsConfiguration.legendsColumnCount = 0;
  let legendBounds = calculateLegendBounds(legendsConfiguration, legendNames, availableWidth);
  if (!legendBounds || hideLegends) return exteriorTopMargin / 2;

  /* should recalculate legend boundaries for x amount of columns if taking up more height than available height  */
  if (legendBounds.height > availableHeight) {
    legendsConfiguration.legendsColumnCount = 3;
    legendBounds = calculateLegendBounds(legendsConfiguration, legendNames, availableWidth);
  }
  if (!legendBounds || legendBounds.bottom <= 0) return 0;
  return legendBounds.bottom + exteriorTopMargin;
};
const calculateMarginBottom = (keyValueSet: KeyValueSet) => {
  const {
    boxWidth,
    boxHeight
  } = keyValueSet.keyRotation === 0 ? keyValueSet.maxHeightKeySize : keyValueSet.maxWidthKeySize;
  const marginBottom = getRotatedBoxHeight(boxWidth, boxHeight, keyValueSet.keyRotation);
  const exteriorPaddingFor90Degrees = exteriorBottomMargin + 4;
  const exteriorMargin = keyValueSet.keyRotation === 90 ? exteriorPaddingFor90Degrees : exteriorBottomMargin;
  return marginBottom > 0 ? marginBottom + exteriorMargin : 0;
};